import { useState } from 'react';
import { Card, Row, Col, Button, Modal, Alert, Badge, Accordion } from 'react-bootstrap';
import { IconTemplate, IconSparkles, IconX, IconCheck } from '@tabler/icons-react';
import { getAllCategoriesWithTemplates } from '../templates/AgentTemplates';

const TemplateSelector = ({ onSelectTemplate, currentValues = {} }) => {
  const [showModal, setShowModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [activeCategory, setActiveCategory] = useState("0");
  const categories = getAllCategoriesWithTemplates();

  const handleTemplateClick = (template) => {
    setSelectedTemplate(template);
    setShowModal(true);
  };

  const handleConfirmTemplate = () => {
    if (selectedTemplate && onSelectTemplate) {
      onSelectTemplate(selectedTemplate);
    }
    setShowModal(false);
    setSelectedTemplate(null);
  };

  const handleCancel = () => {
    setShowModal(false);
    setSelectedTemplate(null);
  };

  // Template em branco
  const blankTemplate = {
    id: "blank",
    name: "Template em Branco",
    description: "Comece do zero com um template vazio",
    icon: "📝",
    color: "light"
  };

  return (
    <>
      <div className="mb-4">
        <h6 className="mb-3">
          <IconTemplate className="me-2" size={16} />
          Escolha um Template para Começar
        </h6>

        <Alert variant="info" className="mb-3">
          <IconSparkles className="me-2" size={16} />
          Selecione um template pré-pronto para preencher automaticamente os
          campos do agente, ou escolha "Template em Branco" para começar do
          zero.
        </Alert>

        {/* Template em Branco sempre visível */}
        <div className="mb-4">
          <h6 className="mb-2">🚀 Opção Personalizada</h6>
          <Row>
            <Col md={4} lg={3} className="mb-3">
              <Card
                className="h-100 border-2 position-relative template-card border-secondary"
                style={{ cursor: "pointer", transition: "all 0.2s" }}
                onClick={() => handleTemplateClick(blankTemplate)}
              >
                <Card.Body className="text-center p-3">
                  <div className="fs-1 mb-2">{blankTemplate.icon}</div>
                  <h6 className="mb-2">{blankTemplate.name}</h6>
                  <p className="small  mb-3" style={{ minHeight: "40px" }}>
                    {blankTemplate.description}
                  </p>
                  <Button variant="outline-secondary" size="sm">
                    <IconSparkles className="me-1" size={14} />
                    Começar do Zero
                  </Button>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </div>

        {/* Templates organizados por categoria */}
        <div>
          <h6 className="mb-3">🎯 Templates Profissionais por Categoria</h6>
          <Accordion activeKey={activeCategory} onSelect={setActiveCategory}>
            {categories.map((category, index) => (
              <Accordion.Item eventKey={String(index)} key={category.key}>
                <Accordion.Header>
                  <div className="d-flex align-items-center justify-content-between w-100 me-3">
                    <div>
                      {category.name}
                      <br />
                      <small className="">{category.description}</small>
                    </div>
                    <Badge bg="primary" className="ms-2">
                      {category.templates.length} templates
                    </Badge>
                  </div>
                </Accordion.Header>
                <Accordion.Body>
                  <Row>
                    {category.templates.map((template) => (
                      <Col md={6} lg={4} key={template.id} className="mb-3">
                        <Card
                          className={`h-100 border-2 position-relative template-card `}
                          style={{ cursor: "pointer", transition: "all 0.2s" }}
                          onClick={() => handleTemplateClick(template)}
                        >
                          <Card.Body className="text-center p-3">
                            <div className="fs-2 mb-2">{template.icon}</div>
                            <h6 className="mb-2">{template.name}</h6>
                            <p
                              className="small  mb-3"
                              style={{ minHeight: "60px" }}
                            >
                              {template.description}
                            </p>

                            <div className="mb-2">
                              <Badge
                                bg={template.color || "primary"}
                                className="me-1 mb-1"
                              >
                                {template.agentArea}
                              </Badge>
                              {template.defaultFlowSteps && (
                                <Badge bg="secondary" className="mb-1">
                                  {template.defaultFlowSteps.length} etapas
                                </Badge>
                              )}
                              {template.customFields &&
                                template.customFields.length > 0 && (
                                  <Badge bg="info" className="ms-1 mb-1">
                                    CRM integrado
                                  </Badge>
                                )}
                            </div>

                            <Button
                              variant={`outline-${template.color || "primary"}`}
                              size="sm"
                            >
                              <IconCheck className="me-1" size={14} />
                              Usar Template
                            </Button>
                          </Card.Body>
                        </Card>
                      </Col>
                    ))}
                  </Row>
                </Accordion.Body>
              </Accordion.Item>
            ))}
          </Accordion>
        </div>
      </div>

      {/* Modal de Confirmação */}
      <Modal show={showModal} onHide={handleCancel} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <IconTemplate className="me-2" size={20} />
            Confirmar Template
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedTemplate && (
            <div>
              <div className="text-center mb-3">
                <div className="fs-1 mb-2">{selectedTemplate.icon}</div>
                <h5>{selectedTemplate.name}</h5>
                <p className="">{selectedTemplate.description}</p>
              </div>

              {selectedTemplate.id === "blank" ? (
                <Alert variant="info">
                  <strong>Template em Branco:</strong>
                  <br />
                  Você começará com campos vazios e poderá configurar tudo
                  manualmente.
                </Alert>
              ) : (
                <>
                  <Alert variant="warning">
                    <strong>Atenção:</strong>
                    <br />
                    Este template irá preencher automaticamente os seguintes
                    campos:
                  </Alert>

                  <div className="mb-3">
                    <h6>Será preenchido automaticamente:</h6>
                    <ul className="mb-0">
                      <li>
                        <strong>Função:</strong> {selectedTemplate.agentJob}
                      </li>
                      <li>
                        <strong>Gênero:</strong> {selectedTemplate.agentGender}
                      </li>
                      <li>
                        <strong>Personalidade:</strong>{" "}
                        {selectedTemplate.agentPersona?.join(", ")}
                      </li>
                      <li>
                        <strong>Área:</strong> {selectedTemplate.agentArea}
                      </li>
                      <li>
                        <strong>Estilo de linguagem</strong> e{" "}
                        <strong>regras fundamentais</strong>
                      </li>
                      <li>
                        <strong>Fluxo de conversa</strong> com{" "}
                        {selectedTemplate.defaultFlowSteps?.length || 0} etapas
                      </li>
                      <li>
                        <strong>Micro-interações</strong> pré-configuradas
                      </li>
                      <li>
                        <strong>Informações do negócio</strong> como template
                      </li>
                    </ul>
                  </div>

                  {Object.keys(currentValues).length > 0 && (
                    <Alert variant="danger">
                      <strong>Dados atuais serão substituídos!</strong>
                      <br />
                      Você já tem alguns campos preenchidos. Ao aplicar este
                      template, todos os dados atuais serão substituídos pelos
                      dados do template.
                    </Alert>
                  )}
                </>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCancel}>
            <IconX className="me-1" size={16} />
            Cancelar
          </Button>
          <Button variant="primary" onClick={handleConfirmTemplate}>
            <IconCheck className="me-1" size={16} />
            {selectedTemplate?.id === "blank"
              ? "Começar do Zero"
              : "Aplicar Template"}
          </Button>
        </Modal.Footer>
      </Modal>

      <style jsx>{`
        .template-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }
      `}</style>
    </>
  );
};

export default TemplateSelector;